import React from 'react';
import { Typography, Skeleton, Badge } from '@vtblife/uikit';

import classname from '@search/classname/src';
import Image from '@search/vtbeco-frontend-core/view/common/components/Image';
import { BanksMortgageOffers } from '../../common/BanksMortgageOffers/BanksMortgageOffers';
import { VTB_ID, MAX_MORTGAGE_DISCOUNT, VTB_BENEFITS } from '../../common/constants';

import './style.css';

const cn = classname.bind(null, 'DesktopMortgageOffers');

export const DesktopMortgageOffers = ({ offers, loading, handleClick, scrollToStart }: {
    offers: BanksMortgageOffers;
    loading: boolean;
    handleClick: () => void;
    scrollToStart: () => void;
}) => {
    const tableRef = React.useRef<HTMLTableElement>(null);
    const [ showAll, setShowAll ] = React.useState(false);
    const [ tableHeight, setTableHeight ] = React.useState(330);

    const handleShowAll = () => {
        setShowAll(state => ! state);
        if (showAll) scrollToStart();
    };

    React.useEffect(() => {
        if (tableRef.current) {
            setTableHeight(showAll ? tableRef?.current.scrollHeight : 330);
        }
    }, [ showAll, offers ]);

    const getTableHeads = () => {
        const tableHeads = [ 'Банк', 'Ставка', 'Платеж' ];

        return (
            <tr className={cn('colHead')}>
                {tableHeads.map((item, index) => (
                    <th key={index}>
                        <Typography variant='small-alone' color='secondary'>
                            {item}
                        </Typography>
                    </th>
                ))}
            </tr>
        );
    };

    const getTableBody = () => {
        if (loading) {
            return (<>
                {[ null, null, null ].map((item, index) => (
                    <tr key={`empty-${index}`} className={cn('row')}>
                        <td className={cn('cell', { empty: true })}>
                            <div style={{ marginBottom: '8px' }}>
                                <Skeleton size='secondary-alone' />
                            </div>
                            <Skeleton size='small-alone' />
                        </td>
                        <td className={cn('cell', { empty: true })}>
                            <div style={{ marginBottom: '8px' }}>
                                <Skeleton size='secondary-alone' />
                            </div>
                            <Skeleton size='small-alone' />
                        </td>
                        <td className={cn('cell', { empty: true })}>
                            <div style={{ marginBottom: '8px' }}>
                                <Skeleton size='secondary-alone' />
                            </div>
                            <div style={{ maxWidth: '40%' }}>
                                <Skeleton size='small-alone' />
                            </div>
                        </td>
                    </tr>
                ))}
            </>);
        }
        return (<>
            {offers.map((offer, index) => (
                <tr key={index} className={cn('row')} onClick={handleClick}>
                    <td className={cn('cell', { name: true })}>
                        <Typography variant='secondary-alone' color='primary' bold className={cn('bankName')}>
                            <Image
                                width={28}
                                height={28}
                                loading='lazy'
                                alt={offer?.name ?? ''}
                                url={offer.icon?.origin}
                            />
                            {offer.name}
                        </Typography>
                        {offer.bankId === VTB_ID && (
                            <div className={cn('benefits')}>
                                {VTB_BENEFITS.map((benefit, idx) => (
                                    <Badge color='lightgray' size='m' key={idx}>
                                        <Typography variant='small-alone' color='secondary'>
                                            {benefit}
                                        </Typography>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </td>
                    <td className={cn('cell')}>
                        {offer.bankId === VTB_ID ? (
                            <div className={cn('percent')}>
                                <Typography variant='primary-alone' color='orange'>
                                    {`от ${offer.minimalPercentFormatted}`}
                                </Typography>
                                <Typography variant='secondary-alone' color='secondary' className={cn('oldPercent')}>
                                    {`${offer.minimalPercent + MAX_MORTGAGE_DISCOUNT}%`.replace('.', ',')}
                                </Typography>
                            </div>
                        ) : (
                            <Typography variant='primary-alone' color='secondary'>
                                {`от ${offer.minimalPercentFormatted}`}
                            </Typography>
                        )}
                    </td>
                    <td className={cn('cell')}>
                        <Typography variant='primary-alone' bold color='primary'>
                            {`от ${offer.minimalPaymentFormatted?.replace('₽', '')} ₽/мес.`}
                        </Typography>
                    </td>
                </tr>
            ))}
        </>);
    };

    return (<>
        <div
            style={offers.length > 3 ? { maxHeight: `${loading ? 330 : tableHeight}px` } : undefined}
            className={cn()}
            ref={tableRef}
        >
            <Typography variant='primary-alone' bold className={cn('title')}>
                Предложения от банков-партнёров
            </Typography>
            <table
                className={cn('table')}
            >
                <thead className={cn('thead')}>
                    {getTableHeads()}
                </thead>
                <tbody>
                    {getTableBody()}
                </tbody>
            </table>

        </div>
        {offers.length > 3 && (
            <Typography variant='secondary-alone' color='link' onClick={handleShowAll} bold className={cn('showAll')}>
                {showAll ? 'Скрыть' : `Показать ещё ${offers.length - 3}`}
            </Typography>)
        }
    </>);
};
